import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSortState, Translate, translate } from 'react-jhipster';
import { RouteComponentProps, Link } from 'react-router-dom';

import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { ITireTreadModel } from 'app/shared/model/tire-tread-model.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import { getEntitiesByNameSearch, reset } from './tire-tread-model.reducer';
import { toast } from 'react-toastify';

export const TireTreadModel = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'), props.location.search)
  );

  const didMount = useRef(false);

  const tireTreadModelList = useAppSelector(state => state.tireTreadModel.entities);
  const loading = useAppSelector(state => state.tireTreadModel.loading);
  const totalItems = useAppSelector(state => state.tireTreadModel.totalItems);
  const [nameSearch, setNameSearch] = useState<string>('');
  const updateSuccess = useAppSelector(state => state.tireTreadModel.updateSuccess);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

const manufacturerComponent = (tireTreadModel: ITireTreadModel) => {
  return tireTreadModel?.tireTreadManufacturer?.name ?? '';
};

  const headers: IHeaderItem[] = [
    { key: 'name', sortable: true, component: <Translate contentKey="fleetSenseApp.tireTreadModel.name" /> },
    { key: 'specification', sortable: true, component: <Translate contentKey="fleetSenseApp.tireTreadModel.specification" /> },
    { key: 'groove', sortable: true, component: <Translate contentKey="fleetSenseApp.tireTreadModel.groove" /> },
    { key: 'tireTreadManufacturer.name', sortable: true, component: <Translate contentKey="fleetSenseApp.tireTreadModel.tireTreadManufacturer" /> },
    { key: 'type', sortable: false, component: <Translate contentKey="fleetSenseApp.tireTreadModel.type" /> },
  ];

  const data: IListData[] = [
    { type: 'text', value: 'name' },
    { type: 'text', value: 'specification' },
    { type: 'text', value: 'groove' },
    { type: 'child', value: '', renderTableChild: manufacturerComponent },
    { type: 'text', value: 'type' },
    { type: 'editButton', value: 'id', showIfCompanyIsOwner: true },
    { type: 'deleteButton', value: 'id', showIfCompanyIsOwner: true },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(reset());
    dispatch(
      getEntitiesByNameSearch({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        nameSearch,
      })
    );
  }, [paginationState]);

  const sortEntities = () => {
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (event, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (updateSuccess) {
      getAllEntities();
    }
  }, [updateSuccess]);

  const searchTypes: ISearchTypes[] = [
    {
      value: nameSearch,
      setValue: setNameSearch,
      placeholder: translate('fleetSenseApp.tireTreadModel.home.search'),
      label: translate('fleetSenseApp.tireTreadModel.home.search'),
    },
  ];

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
      <PageHeader
        headingId="tire-tread-manufacturer-heading"
        headingDataCy="TireTreadManufacturerHeading"
        contentKey="fleetSenseApp.tireTreadModel.home.title"
        match={match}
        showButton={false}
        actions={
          <>
            <Button
              component={Link}
              color="secondary"
              variant="contained"
              to="/tire-tread-manufacturer"
              startIcon={<ArrowRightAltIcon />}
            >
              <Translate contentKey="fleetSenseApp.tireTreadManufacturer.home.title" />
            </Button>

            <Button
              component={Link}
              color="secondary"
              variant="contained"
              to={`${match.url}/new`}
              id="jh-create-entity"
              data-cy="entityCreateButton"
              startIcon={<AddIcon />}
            >
              <Translate contentKey="fleetSenseApp.tireTreadModel.home.createLabel" />
            </Button>
          </>
        }
      />

      <PageFilters setActivePage={setActivePage} searchTypes={searchTypes} isCompany={false} showFilters={['filters']} />
      {!loading && <TableComponent listData={data} list={tireTreadModelList} headers={headers} match={match} sortFunction={sort} pagination={paginationState} />}
      {!loading && !tireTreadModelList.length && <NoData title="Nenhum dado encontrado" />}
      {loading && <Loading />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={tireTreadModelList} />
    </Box>
  );
};

export default TireTreadModel;